'use client'

import {
  Children,
  createContext,
  forwardRef,
  KeyboardEvent,
  ReactNode,
  Ref,
  useContext,
  useEffect,
  useRef,
} from 'react'

import { PktTabItem } from './TabItem'

export type TSkin = 'blue' | 'green' | 'red' | 'beige' | 'yellow' | 'grey' | 'gray' | 'blue-light'

// Context for passing tab navigation logic to children
interface ITabsContext {
  arrowNav: boolean
  registerTabRef: (index: number, el: HTMLAnchorElement | HTMLButtonElement | null) => void
  handleKeyPress: (index: number, event: KeyboardEvent) => void
  selectTab: (index: number) => void
}

const TabsContext = createContext<ITabsContext | null>(null)

export const useTabsContext = () => {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('TabItem must be used within a Tabs component')
  }
  return context
}

export interface IPktTab {
  text: string
  href?: string
  action?: (index: number) => void
  icon?: string
  controls?: string
  tag?: {
    text: string
    skin: TSkin | undefined
  }
  active?: boolean
}

export interface IPktTabs {
  arrowNav?: boolean
  disableArrowNav?: boolean
  tabs?: IPktTab[]
  onTabSelected?: (index: number) => void
  children?: ReactNode
}

export const PktTabs = forwardRef(
  (
    { arrowNav = true, disableArrowNav = false, tabs, onTabSelected, children }: IPktTabs,
    ref: Ref<HTMLDivElement>,
  ): JSX.Element => {
    const tabRefs = useRef<Array<HTMLAnchorElement | HTMLButtonElement | null>>([])

    const useArrowNav = arrowNav && !disableArrowNav

    // Determine if we're using children or tabs array
    const hasChildren = children && Children.count(children) > 0
    const tabCount = hasChildren ? Children.count(children) : tabs?.length || 0

    useEffect(() => {
      tabRefs.current = tabRefs.current.slice(0, tabCount)
    }, [tabCount])

    const selectTab = (index: number): void => {
      const tab = tabs?.[index]
      if (tab?.action) {
        tab.action(index)
      }
      if (onTabSelected) onTabSelected(index)
    }

    const handleKeyPress = (index: number, event: KeyboardEvent) => {
      if (useArrowNav) {
        if (event.code === 'ArrowLeft' && index !== 0) {
          tabRefs.current[index - 1]?.focus()
        }
        if (event.code === 'ArrowRight' && index < tabCount - 1) {
          tabRefs.current[index + 1]?.focus()
        }
        if (event.code === 'ArrowDown' || event.code === 'Space') {
          selectTab(index)
        }
      }
    }

    const registerTabRef = (index: number, el: HTMLAnchorElement | HTMLButtonElement | null) => {
      tabRefs.current[index] = el
    }

    // If tabs as prop instead of children
    const tabItems = tabs?.map((tab, index) => (
      <PktTabItem
        key={index}
        active={tab.active}
        href={tab.href}
        onClick={() => selectTab(index)}
        icon={tab.icon}
        controls={tab.controls}
        tag={tab.tag?.text}
        tagSkin={tab.tag?.skin}
        index={index}
      >
        {tab.text}
      </PktTabItem>
    ))

    return (
      <TabsContext.Provider value={{ arrowNav: useArrowNav, registerTabRef, handleKeyPress, selectTab }}>
        <div className="pkt-tabs" ref={ref}>
          <div className="pkt-tabs__list" role={useArrowNav ? 'tablist' : 'navigation'}>
            {children || tabItems}
          </div>
        </div>
      </TabsContext.Provider>
    )
  },
)

export { PktTabItem } from './TabItem'

PktTabs.displayName = 'PktTabs'
